import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../../db/db";
import { acknowledgeConflict, listLocalConflicts } from "../../db/repo";
import { syncNow, AUTH_PROVIDER, type SyncSummary } from "../../lib/sync/syncClient";
import { Button, Card, RiskTag, SectionTitle } from "../../components/ui";

export function AccountSyncSection() {
  const isLocal = AUTH_PROVIDER.kind === "local";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accountLabel, setAccountLabel] = useState<string | null>(null);
  const [summary, setSummary] = useState<SyncSummary | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflicts, setConflicts] = useState<Awaited<ReturnType<typeof listLocalConflicts>>>([]);

  const pendingCount = useLiveQuery(
    () => db.syncEvents.where("syncStatus").equals("pending").count(),
    [],
    0,
  );

  const refreshAccount = async () => {
    try {
      const user = await AUTH_PROVIDER.getCurrentUser();
      setAccountLabel(user ? (user.isLocal ? null : user.email ?? user.id) : null);
    } catch {
      setAccountLabel(null);
    }
  };
  const refreshConflicts = async () => {
    setConflicts(await listLocalConflicts());
  };

  useMemo(() => {
    void refreshAccount();
    void refreshConflicts();
  }, []);

  const doSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      const s = await syncNow();
      setSummary(s);
      await refreshConflicts();
    } catch (e) {
      setError(e instanceof Error ? e.message : "同步失败（网络或服务端不可用）");
      setSummary(null);
    } finally {
      setSyncing(false);
    }
  };

  const doSignIn = async () => {
    setError(null);
    try {
      const user = await AUTH_PROVIDER.signIn(email.trim(), password);
      if ("kind" in AUTH_PROVIDER && AUTH_PROVIDER.kind === "supabase") {
        setSummary({ mode: "supabase", uploaded: 0, applied: 0, conflicts: 0, message: `已登录 ${user.email ?? user.id}，开始同步…` });
        await doSync();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "登录失败");
    }
  };

  const unacked = conflicts.filter((c) => !c.acknowledged);

  return (
    <>
      <SectionTitle>账户与同步状态</SectionTitle>
      <Card className="p-4 mb-4 space-y-3.5 border border-tone-border shadow-sm">
        {/* 账户状态 */}
        <div className="flex items-center justify-between">
          <span className="text-[14px] font-semibold text-tone-ink">账户模式</span>
          {isLocal ? (
            <RiskTag tone="warn">本地模式</RiskTag>
          ) : accountLabel ? (
            <span className="text-[13px] text-tone-ink font-mono">{accountLabel}</span>
          ) : (
            <RiskTag tone="accent">未登录</RiskTag>
          )}
        </div>
        {isLocal ? (
          <p className="text-[12px] text-tone-soft leading-relaxed">
            尚未配置云端服务（VITE_SUPABASE_URL / ANON_KEY）。当前数据仅保存在本设备，
            <strong>没有伪造“云端账户”</strong>；配置后登录即可跨设备同步（iPhone / Android）。
          </p>
        ) : accountLabel ? (
          <Button variant="ghost" className="w-full" onClick={() => void AUTH_PROVIDER.signOut().then(refreshAccount)}>
            退出登录
          </Button>
        ) : (
          <div className="space-y-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="邮箱"
              className="w-full min-h-[44px] px-4 rounded-xl bg-tone-card border border-tone-border text-tone-ink text-sm"
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="密码"
              className="w-full min-h-[44px] px-4 rounded-xl bg-tone-card border border-tone-border text-tone-ink text-sm"
            />
            <Button className="w-full" disabled={!email || !password || syncing} onClick={() => void doSignIn()}>
              登录并同步
            </Button>
          </div>
        )}

        {/* 同步状态 */}
        <div className="flex items-center justify-between border-t border-tone-border pt-3">
          <div>
            <div className="text-[14px] font-semibold text-tone-ink">出站队列</div>
            <div className="text-[12px] text-tone-soft">待上传 {pendingCount} 条 · 离线可用，联网补同步</div>
          </div>
          <Button variant="secondary" onClick={() => void doSync()} disabled={syncing || isLocal}>
            {syncing ? "同步中…" : "立即同步"}
          </Button>
        </div>
        {summary ? (
          <p className="text-[12px] text-tone-good font-semibold">{summary.message}</p>
        ) : error ? (
          <p className="text-[12px] text-tone-danger font-semibold">{error}</p>
        ) : null}
        <div className="flex items-center justify-between text-[12px] text-tone-soft pt-1">
          <span>设备标识</span>
          <span className="font-mono">device-local-1（本地模式）</span>
        </div>
      </Card>

      {/* 冲突留档（ADR-013：保留双版本 + 简化 UI） */}
      <SectionTitle>数据冲突审计</SectionTitle>
      <Card className="p-4 mb-4 border border-tone-border shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[14px] font-semibold text-tone-ink">双版本保留记录</span>
          {unacked.length > 0 ? <RiskTag tone="warn">{unacked.length} 条待处理</RiskTag> : <RiskTag tone="good">无冲突</RiskTag>}
        </div>
        {unacked.length === 0 ? (
          <p className="text-[12px] text-tone-soft leading-relaxed">
            正常合并中：伤情取更严格值、用户修正优先、已完成训练不回退、同组双端编辑保留双版本。
          </p>
        ) : (
          <div className="space-y-2.5">
            {unacked.slice(0, 5).map((c) => (
              <div key={`${c.entityType}:${c.entityId}:${c.createdAt}`} className="rounded-xl border border-tone-warn/40 bg-tone-warn/5 p-3 text-[12px] space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-tone-ink font-semibold">{c.entityType} · {c.entityId.slice(0, 8)}</span>
                  <span className="text-tone-soft font-mono">{c.resolution}</span>
                </div>
                <p className="text-tone-soft">{c.reasonCodes.join(" / ")}</p>
                <Button
                  variant="ghost"
                  className="w-full mt-1 min-h-[36px] text-xs font-semibold"
                  onClick={() => void acknowledgeConflict(c.entityType, c.entityId, c.createdAt).then(refreshConflicts)}
                >
                  已了解（双版本均已保留）
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}

export function SettingsAccount() {
  const nav = useNavigate();

  return (
    <div className="min-h-screen mx-auto max-w-[480px] px-4 pt-safe-top pb-10">
      {/* 顶部返回导航栏 */}
      <div className="flex items-center justify-between py-3 mb-2">
        <button
          onClick={() => nav(-1)}
          className="inline-flex items-center gap-1 text-tone-accent font-semibold text-sm active:opacity-70"
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          返回
        </button>
        <h1 className="text-[17px] font-bold text-tone-ink">账户与同步</h1>
        <div className="w-12" />
      </div>

      <AccountSyncSection />
    </div>
  );
}
